import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Text, Card } from '@teals/ui-web';
import { useAppDispatch } from '../../store';
import { setCredentials } from '../../store/slices';
import { useCreateOrganisationMutation } from '../../store/api/portalApi';
import { useAuth } from '../../hooks/useAuth';
import { refreshSession } from '../../services/cognito';

const BUSINESS_TYPES = [
  { value: 'general', label: 'General business' },
  { value: 'individual', label: 'Individual / sole trader' },
  { value: 'agency', label: 'Agency' },
] as const;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
  padding: 2rem 1.5rem;
`;

const SetupCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--bg-danger-subtle, #fef2f2);
  border: 1px solid var(--border-danger, #fecaca);
  border-radius: 0.5rem;
  color: var(--text-danger, #dc2626);
  font-size: 0.875rem;
`;

const CreateOrganisation: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, token, accessToken, refreshToken } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [businessType, setBusinessType] = useState<string>('general');
  const [errorMessage, setErrorMessage] = useState('');

  const [createOrganisation, { isLoading }] = useCreateOrganisationMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setErrorMessage('');
    try {
      const org = await createOrganisation({
        name: name.trim(),
        description: description.trim() || undefined,
        businessType,
      }).unwrap();

      const refreshed = await refreshSession();
      if (refreshed && user) {
        dispatch(
          setCredentials({
            user: {
              ...user,
              organizationId: refreshed.user.organizationId || org.id,
            },
            token: refreshed.tokens.idToken,
            accessToken: refreshed.tokens.accessToken,
            refreshToken: refreshed.tokens.refreshToken,
          })
        );
      } else if (user && token) {
        dispatch(
          setCredentials({
            user: { ...user, organizationId: org.id },
            token,
            accessToken: accessToken ?? undefined,
            refreshToken: refreshToken ?? undefined,
          })
        );
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message || 'Failed to create organisation.')
          : err instanceof Error
            ? err.message
            : 'Failed to create organisation. Please try again.';
      setErrorMessage(message);
    }
  };

  return (
    <Page>
      <SetupCard padding={32} elevation="md">
        <div>
          <Text variant="h3">Set Up Your Organisation</Text>
          <Subtitle>
            Every Luminafactory account needs an organisation before you can build agents,
            manage partners, or invite team members.
          </Subtitle>
        </div>

        {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

        <Form onSubmit={handleSubmit}>
          <Input
            label="Organisation Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Labs"
            fullWidth
            required
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does your organisation do?"
            fullWidth
          />
          <SelectWrapper>
            <SelectLabel htmlFor="business-type">Business Type</SelectLabel>
            <Select
              id="business-type"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
            >
              {BUSINESS_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </SelectWrapper>
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || isLoading}
            fullWidth
          >
            {isLoading ? 'Creating...' : 'Create Organisation'}
          </Button>
        </Form>
      </SetupCard>
    </Page>
  );
};

export default CreateOrganisation;
